import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Flame } from "lucide-react";

export function StreakBadge({
  streak,
  bestStreak,
  lastTipDate,
  freezeUsedAt,
}: {
  streak: number;
  bestStreak: number;
  lastTipDate: string | null;
  freezeUsedAt: string | null;
}) {
  const [open, setOpen] = useState(false);
  const today = new Date().toISOString().slice(0, 10);
  const tippedToday = lastTipDate === today;
  const freezeAvailable =
    !freezeUsedAt || (Date.now() - new Date(freezeUsedAt).getTime()) > 7 * 24 * 60 * 60 * 1000;

  const isZero = streak === 0;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className={`inline-flex items-center gap-1 backdrop-blur rounded-pill px-2.5 py-1 text-xs font-bold tap transition ${
          isZero
            ? "bg-primary-foreground/10 text-primary-foreground/80 hover:bg-primary-foreground/20"
            : "bg-primary-foreground/15 hover:bg-primary-foreground/25"
        }`}
      >
        <motion.span
          animate={tippedToday ? { rotate: [0, -10, 10, -10, 0], scale: [1, 1.15, 1] } : {}}
          transition={{ duration: 0.8, repeat: Infinity, repeatDelay: 3 }}
          className={`inline-block ${isZero ? "grayscale opacity-70" : ""}`}
        >
          🔥
        </motion.span>
        {isZero ? <span>Streak starten</span> : <span className="tabular-nums">{streak}</span>}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-primary" /> Deine Streak
            </DialogTitle>
            <DialogDescription>
              Tippe jeden Tag, an dem ein Spiel ist, um deine Streak zu halten.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="bg-card rounded-lg p-4 text-center">
              <div className="font-display font-extrabold text-5xl">🔥 {streak}</div>
              <div className="text-xs text-muted-foreground mt-1">Aktuelle Streak</div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Stat label="Bestleistung" value={`🏆 ${bestStreak}`} />
              <Stat label="Heute" value={tippedToday ? "✓ getippt" : "noch offen"} />
            </div>
            <div className={`rounded-md p-3 text-xs ${freezeAvailable ? "bg-accent/15 border border-accent/30" : "bg-muted"}`}>
              {freezeAvailable ? (
                <>❄️ <b>Streak-Schutz aktiv</b> — verpasst du einmal einen Tag, wird deine Streak einmalig geschützt.</>
              ) : (
                <>❄️ Streak-Schutz wird in den nächsten Tagen wieder verfügbar.</>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-muted/50 rounded-md p-2 text-center">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{label}</div>
      <div className="font-bold text-sm mt-1">{value}</div>
    </div>
  );
}
